import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { LegendItem } from "../../components/metrics/metricsUi";
import {
  calendarLabel,
  coverageColor,
  fmtCompact,
  fmtDay,
  fmtPct,
  fmtUnits,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import { SectionCard, tooltipContentStyle } from "./parts";

const PAST_KINDS = ["unconfirmed", "overdue", "no_date"];
// Buckets with no future date are coloured by WHY they have none (same status
// colours as coverage), so the past-due story reads before any number does.
const PAST_STATUS = { unconfirmed: "unconfirmed", overdue: "late", no_date: "pending" };

const BucketTooltip = ({ active, payload, ui }) => {
  if (!active || !payload || !payload.length) return null;
  const bucket = payload[0].payload;
  const title =
    bucket.kind === "week"
      ? `Week of ${fmtDay(bucket.start)}`
      : bucket.kind === "later"
        ? `After ${fmtDay(bucket.start)}`
        : bucket.kind === "unconfirmed"
          ? "Overdue by more than 30 days (not counted as supply)"
          : bucket.kind === "overdue"
            ? "Overdue by up to 30 days"
            : "On order with no usable expected date";
  return (
    <Box sx={{ ...tooltipContentStyle(ui), p: 1.25, maxWidth: 260 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 650, mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12 }}>{fmtUnits(bucket.units)} units · {fmtUnits(bucket.pos)} POs · {fmtUnits(bucket.skus)} SKUs</Typography>
      <Typography sx={{ fontSize: 11.5, color: ui.colors.textSecondary }}>
        In transit {fmtUnits(bucket.moving_units)} · Placed {fmtUnits(bucket.placed_units)}
      </Typography>
      {bucket.units ? (
        <Typography sx={{ fontSize: 11, color: ui.colors.textTertiary, mt: 0.5 }}>Click to list these POs</Typography>
      ) : null}
    </Box>
  );
};

const InboundCalendar = ({ ui, overview, onPickBucket, activeBucket }) => {
  const { colors } = ui;
  const { pipeline, calendar } = overview;
  const movingColor = ui.accent;
  const placedColor = ui.darkMode ? "#8FAEF0" : "#A9BEF5";
  const pastShade = ui.darkMode ? "rgba(220,80,104,0.10)" : "rgba(181,32,58,0.06)";
  const data = calendar.map((bucket) => ({ ...bucket, label: calendarLabel(bucket) }));
  const datedShare = pipeline.open_units ? (pipeline.scheduled_units / pipeline.open_units) * 100 : null;
  const lastPast = data.filter((bucket) => PAST_KINDS.includes(bucket.kind)).pop();

  const headline = !pipeline.open_units
    ? `Nothing on order for this warehouse${pipeline.last_po_date ? ` — last PO raised ${fmtDay(pipeline.last_po_date)}` : ""}.`
    : datedShare === 0
      ? `None of the ${fmtUnits(pipeline.open_units)} units on order has a future arrival date — every open PO is overdue or undated.`
      : `${fmtPct(datedShare)} of the ${fmtUnits(pipeline.open_units)} units on order have a future arrival date.`;

  return (
    <SectionCard ui={ui} title="Inbound calendar" subtitle={headline} sx={{ height: "100%" }}>
      <Box sx={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 4, left: -8, bottom: 0 }} barCategoryGap="22%">
            <CartesianGrid vertical={false} stroke={colors.border} />
            {lastPast ? (
              <ReferenceArea
                x1={data[0].label}
                x2={lastPast.label}
                fill={pastShade}
                ifOverflow="extendDomain"
              />
            ) : null}
            <XAxis
              dataKey="label"
              interval={0}
              tick={{ fontSize: 10, fill: colors.textSecondary }}
              tickLine={false}
              axisLine={{ stroke: colors.border }}
              angle={-35}
              textAnchor="end"
              height={46}
            />
            <YAxis
              tickFormatter={fmtCompact}
              tick={{ fontSize: 10.5, fill: colors.textSecondary }}
              tickLine={false}
              axisLine={false}
              width={48}
            />
            <RechartsTooltip cursor={{ fill: ui.surfaceAlt }} content={<BucketTooltip ui={ui} />} />
            {[
              { key: "moving_units", name: "In transit / partly received", color: movingColor, top: false },
              { key: "placed_units", name: "Placed, not shipped", color: placedColor, top: true },
            ].map((series) => (
              <Bar
                key={series.key}
                dataKey={series.key}
                name={series.name}
                stackId="units"
                maxBarSize={24}
                isAnimationActive={false}
                radius={series.top ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                onClick={(entry) => {
                  // Recharts passes the bar's props; the bucket is its payload.
                  const bucket = entry && (entry.payload || entry);
                  if (bucket && bucket.units) onPickBucket(bucket.key);
                }}
                cursor="pointer"
              >
                {data.map((bucket) => (
                  <Cell
                    key={bucket.key}
                    fill={PAST_STATUS[bucket.kind] ? coverageColor(PAST_STATUS[bucket.kind], ui.darkMode) : series.color}
                    fillOpacity={activeBucket && activeBucket !== bucket.key ? 0.35 : 1}
                    // Surface-coloured edge = the gap between stacked segments.
                    stroke={ui.colors.bgPaper}
                    strokeWidth={2}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
        <LegendItem color={movingColor} label="Due: in transit / partly received" />
        <LegendItem color={placedColor} label="Due: placed, not shipped" />
        <LegendItem color={coverageColor("late", ui.darkMode)} label="Overdue ≤30d" />
        <LegendItem color={coverageColor("unconfirmed", ui.darkMode)} label="Stale >30d (not counted)" />
        <LegendItem color={coverageColor("pending", ui.darkMode)} label="No date" />
      </Box>
      <Typography sx={{ fontSize: 11.5, color: colors.textTertiary, mt: 1, lineHeight: 1.55 }}>
        Units still outstanding at this warehouse, by expected arrival week (portal POs: promised dispatch
        week). Last PO raised {fmtDay(pipeline.last_po_date)} · last receipt {fmtDay(pipeline.last_received_date)}.
      </Typography>
    </SectionCard>
  );
};

export default InboundCalendar;
